import { useEffect, useMemo, useState } from "react";
import {
    Box, Stack, Input, Select, Option, Button, Sheet, Switch, Typography, Chip,
    Dropdown, Menu, MenuButton, MenuItem, IconButton, Modal,
    FormControl, FormLabel, Divider,
} from "@mui/joy";
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

const EMPTY = {
    search: '', manager: '', surveyor: '', draftsman: '', city: '', region: '',
    created_by: '', stage_changed_by: '', modified_by: '',
    amount_field: 'final_price', amount_op: 'exact', amount: '', amount_from: '', amount_to: '',
    date_event_from: '', date_event_to: '',
    date_completed_from: '', date_completed_to: '',
    date_create_from: '', date_create_to: '',
    deadline: '',
};

// Ключи, которые считаем «активным фильтром» (search показываем отдельно как строку поиска)
const FILTER_KEYS = [
    'manager', 'surveyor', 'draftsman', 'city', 'region',
    'created_by', 'stage_changed_by', 'modified_by',
    'amount', 'amount_from', 'amount_to',
    'date_event_from', 'date_event_to',
    'date_completed_from', 'date_completed_to',
    'date_create_from', 'date_create_to',
    'deadline',
];

// Убираем пустые значения; amount_field/op без суммы не отправляем
function clean(draft) {
    const out = {};
    Object.entries(draft).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    if (!out.amount && !out.amount_from && !out.amount_to) {
        delete out.amount_field;
        delete out.amount_op;
    }
    return out;
}

export default function KanbanFilterBar({
    value, options, saved = [], onApply, onReset, onSavePreset, onDeletePreset, onApplyPreset,
}) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ ...EMPTY, ...(value || {}) });

    // модалка «Сохранить фильтр»
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saveShared, setSaveShared] = useState(false);
    const [saveDefault, setSaveDefault] = useState(false);

    useEffect(() => { setDraft({ ...EMPTY, ...(value || {}) }); }, [value]);

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const openSave = () => { setSaveName(''); setSaveShared(false); setSaveDefault(false); setSaveOpen(true); };
    const doSave = () => {
        const name = saveName.trim();
        if (!name) return;
        onSavePreset?.({ name, params: clean(draft), is_shared: saveShared, is_default: saveDefault });
        setSaveOpen(false);
    };

    const emps = options?.employees || [];
    const has = (e, pos) => Array.isArray(e.positions) && e.positions.includes(pos);
    const managers  = emps.filter((e) => has(e, 'manager') || has(e, 'head_manager') || e.role === 'manager' || e.role === 'administrator');
    const surveyors = emps.filter((e) => has(e, 'surveyor'));
    const draftsmen = emps.filter((e) => has(e, 'draftsman'));
    const cities  = options?.cities  || [];
    const regions = options?.regions || [];
    const amountOps = options?.amount_ops || [
        { key: 'exact', label: 'Точно' }, { key: 'gte', label: 'От' },
        { key: 'lte', label: 'До' }, { key: 'range', label: 'Диапазон' },
    ];

    const activeCount = useMemo(
        () => FILTER_KEYS.filter((k) => value && value[k]).length,
        [value]
    );

    const apply = () => { onApply?.(clean(draft)); setOpen(false); };
    const reset = () => { setDraft({ ...EMPTY }); onReset?.(); setOpen(false); };
    const applySearch = () => onApply?.(clean(draft)); // поиск по Enter, без открытия панели

    // Функции-рендеры (не под-компоненты — иначе поля теряют фокус)
    const empSelect = (label, k, list) => (
        <FormControl size="sm" sx={{ flex: '1 1 200px', minWidth: 180 }}>
            <FormLabel>{label}</FormLabel>
            <Select value={draft[k] || ''} onChange={(_, v) => set(k, v || '')}
                placeholder="Любой" slotProps={{ listbox: { sx: { zIndex: 10102 } } }}>
                <Option value="">Любой</Option>
                {list.map((e) => <Option key={e.id} value={String(e.id)}>{e.name}</Option>)}
            </Select>
        </FormControl>
    );

    const dictSelect = (label, k, list) => (
        <FormControl size="sm" sx={{ flex: '1 1 200px', minWidth: 180 }}>
            <FormLabel>{label}</FormLabel>
            <Select value={draft[k] || ''} onChange={(_, v) => set(k, v || '')}
                placeholder="Любой" slotProps={{ listbox: { sx: { zIndex: 10102 } } }}>
                <Option value="">Любой</Option>
                {list.map((it) => <Option key={it.id} value={String(it.id)}>{it.name}</Option>)}
            </Select>
        </FormControl>
    );

    const dateRange = (label, from, to) => (
        <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <FormLabel sx={{ mb: 0.5 }}>{label}</FormLabel>
            <Stack direction="row" spacing={1}>
                <Input size="sm" type="date" value={draft[from] || ''} onChange={(e) => set(from, e.target.value)} sx={{ flex: 1 }} />
                <Input size="sm" type="date" value={draft[to] || ''} onChange={(e) => set(to, e.target.value)} sx={{ flex: 1 }} />
            </Stack>
        </Box>
    );

    return (
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Input
                    size="sm"
                    value={draft.search || ''}
                    onChange={(e) => set('search', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                    startDecorator={<SearchRoundedIcon />}
                    endDecorator={<IconButton aria-label="Найти на доске" size="sm" variant="plain" onClick={applySearch}><SearchRoundedIcon /></IconButton>}
                    placeholder="№ заявки/сделки, ФИО, адрес, программа…"
                    slotProps={{ input: { maxLength: 256, 'aria-label': 'Поиск по всем данным воронки' } }}
                    sx={{ flex: 1, minWidth: 240, maxWidth: 460 }}
                />
                <Button size="sm" variant={activeCount ? 'solid' : 'outlined'} color={activeCount ? 'primary' : 'neutral'}
                        startDecorator={<FilterAltRoundedIcon />} onClick={() => setOpen((o) => !o)}>
                    Фильтр{activeCount ? ` · ${activeCount}` : ''}
                </Button>

                {saved && saved.length > 0 && (
                    <Dropdown>
                        <MenuButton slots={{ root: Button }}
                            slotProps={{ root: { size: 'sm', variant: 'outlined', color: 'neutral', startDecorator: <BookmarkBorderRoundedIcon /> } }}>
                            Пресеты
                        </MenuButton>
                        <Menu size="sm" sx={{ zIndex: 10102, minWidth: 240 }}>
                            {saved.map((f) => (
                                <MenuItem key={f.id} onClick={() => onApplyPreset?.(f)} sx={{ gap: 0.5 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography level="body-sm" noWrap>{f.name}</Typography>
                                    </Box>
                                    {f.is_shared && <Chip size="sm" variant="soft" color="primary">общий</Chip>}
                                    {f.is_mine && (
                                        <IconButton size="sm" variant="plain" color="danger"
                                            onClick={(e) => { e.stopPropagation(); onDeletePreset?.(f); }}>
                                            <DeleteOutlineRoundedIcon fontSize="sm" />
                                        </IconButton>
                                    )}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Dropdown>
                )}
                {(activeCount || draft.search) ? (
                    <Button size="sm" variant="plain" color="neutral" startDecorator={<CloseRoundedIcon />} onClick={reset}>
                        Сбросить
                    </Button>
                ) : null}
            </Stack>

            {open && (
                <Sheet variant="outlined" sx={{ mt: 1, p: 2, borderRadius: 'lg' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {empSelect('Ответственный', 'manager', managers)}
                        {empSelect('Обмерщик', 'surveyor', surveyors)}
                        {empSelect('Чертёжник', 'draftsman', draftsmen)}
                        {dictSelect('Город', 'city', cities)}
                        {dictSelect('Регион', 'region', regions)}
                        <FormControl size="sm" sx={{ flex: '1 1 140px', minWidth: 130 }}>
                            <FormLabel>Дедлайн</FormLabel>
                            <Select value={draft.deadline || ''} onChange={(_, v) => set('deadline', v || '')}
                                slotProps={{ listbox: { sx: { zIndex: 10102 } } }}>
                                <Option value="">Любой</Option>
                                <Option value="overdue">Просрочен</Option>
                                <Option value="has">Есть</Option>
                                <Option value="none">Нет</Option>
                            </Select>
                        </FormControl>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Атрибуция (Этап 4): кем создана / кто изменил стадию / кем изменена */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {empSelect('Кем создана', 'created_by', emps)}
                        {empSelect('Кто изменил стадию', 'stage_changed_by', emps)}
                        {empSelect('Кем изменена', 'modified_by', emps)}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
                        <FormControl size="sm" sx={{ flex: '1 1 180px', minWidth: 160 }}>
                            <FormLabel>Сумма — поле</FormLabel>
                            <Select value={draft.amount_field || 'final_price'} onChange={(_, v) => set('amount_field', v || 'final_price')}
                                slotProps={{ listbox: { sx: { zIndex: 10102 } } }}>
                                <Option value="final_price">Итоговая стоимость</Option>
                                <Option value="opportunity">Сумма сделки</Option>
                            </Select>
                        </FormControl>
                        <FormControl size="sm" sx={{ flex: '0 1 140px', minWidth: 120 }}>
                            <FormLabel>Условие</FormLabel>
                            <Select value={draft.amount_op || 'exact'} onChange={(_, v) => set('amount_op', v || 'exact')}
                                slotProps={{ listbox: { sx: { zIndex: 10102 } } }}>
                                {amountOps.map((o) => <Option key={o.key} value={o.key}>{o.label}</Option>)}
                            </Select>
                        </FormControl>
                        {draft.amount_op === 'range' ? (
                            <>
                                <FormControl size="sm" sx={{ flex: '1 1 120px', minWidth: 110 }}>
                                    <FormLabel>От</FormLabel>
                                    <Input type="number" value={draft.amount_from || ''} onChange={(e) => set('amount_from', e.target.value)} />
                                </FormControl>
                                <FormControl size="sm" sx={{ flex: '1 1 120px', minWidth: 110 }}>
                                    <FormLabel>До</FormLabel>
                                    <Input type="number" value={draft.amount_to || ''} onChange={(e) => set('amount_to', e.target.value)} />
                                </FormControl>
                            </>
                        ) : (
                            <FormControl size="sm" sx={{ flex: '1 1 160px', minWidth: 140 }}>
                                <FormLabel>Значение (₽)</FormLabel>
                                <Input type="number" value={draft.amount || ''} onChange={(e) => set('amount', e.target.value)} />
                            </FormControl>
                        )}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {dateRange('Дата события', 'date_event_from', 'date_event_to')}
                        {dateRange('Дата завершения', 'date_completed_from', 'date_completed_to')}
                        {dateRange('Дата создания', 'date_create_from', 'date_create_to')}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
                        <Button size="sm" onClick={apply} startDecorator={<SearchRoundedIcon />}>Найти</Button>
                        <Button size="sm" variant="outlined" color="neutral" onClick={reset}>Сбросить</Button>
                        <Box sx={{ flex: 1 }} />
                        <Button size="sm" variant="soft" color="primary" startDecorator={<SaveRoundedIcon />} onClick={openSave}>
                            Сохранить фильтр
                        </Button>
                    </Stack>
                </Sheet>
            )}

            <Modal open={saveOpen} onClose={() => setSaveOpen(false)} sx={{ zIndex: 10200 }}>
                <Sheet variant="outlined" sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 360, p: 2.5, borderRadius: 'lg',
                }}>
                    <Typography level="title-md" sx={{ mb: 1.5 }}>Сохранить фильтр</Typography>
                    <FormControl size="sm" sx={{ mb: 1.5 }}>
                        <FormLabel>Название</FormLabel>
                        <Input autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)}
                            placeholder="Напр. «Мои просрочки»"
                            onKeyDown={(e) => { if (e.key === 'Enter') doSave(); }} />
                    </FormControl>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Switch size="sm" checked={saveShared} onChange={(e) => setSaveShared(e.target.checked)} />
                        <Typography level="body-sm">Общий (виден всем сотрудникам)</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Switch size="sm" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} />
                        <Typography level="body-sm">Применять по умолчанию</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="sm" variant="plain" color="neutral" onClick={() => setSaveOpen(false)}>Отмена</Button>
                        <Button size="sm" onClick={doSave} disabled={!saveName.trim()}>Сохранить</Button>
                    </Stack>
                </Sheet>
            </Modal>
        </Box>
    );
}